import toast from 'react-hot-toast';

// Determine API URL: Use relative path when served from domain, otherwise use env or localhost
const getApiUrl = () => {
    // If we're on a domain (not localhost), always use relative path (nginx will proxy /api)
    // This avoids mixed content issues (HTTP page trying to load HTTPS resources)
    if (typeof window !== 'undefined' &&
        window.location.hostname !== 'localhost' &&
        window.location.hostname !== '127.0.0.1' &&
        !window.location.hostname.startsWith('192.168.') &&
        !window.location.hostname.startsWith('10.') &&
        !window.location.hostname.startsWith('172.')) {
        return '/api';
    }

    // For localhost/development, use env URL or default
    const envUrl = import.meta.env.VITE_API_URL;
    if (envUrl && (envUrl.startsWith('http://') || envUrl.startsWith('https://'))) {
        return envUrl;
    }

    // Default to localhost for development
    return envUrl || 'http://localhost:5000/api';
};

const API_URL = getApiUrl();
const getApiOrigin = () => {
    if (API_URL.startsWith('http://') || API_URL.startsWith('https://')) {
        try {
            return new URL(API_URL).origin;
        } catch (error) {
            return '';
        }
    }
    if (typeof window !== 'undefined') {
        return window.location.origin;
    }
    return '';
};

export const resolveMediaUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    const trimmed = url.trim();
    if (!trimmed) return '';

    // Block known inappropriate/deprecated media assets.
    const normalized = trimmed.toLowerCase();
    if (normalized.includes('graduation_banner')) {
        return '';
    }

    if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:') || trimmed.startsWith('blob:')) {
        return trimmed;
    }

    const origin = getApiOrigin();
    if (trimmed.startsWith('/uploads/')) {
        return origin ? `${origin}${trimmed}` : trimmed;
    }
    if (trimmed.startsWith('uploads/')) {
        return origin ? `${origin}/${trimmed}` : `/${trimmed}`;
    }
    return trimmed;
};

console.log('🌐 API URL configured:', API_URL, {
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'N/A',
    envUrl: import.meta.env.VITE_API_URL
});

// Helper function for API calls
const apiRequest = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token');

    const headers = {
        'Content-Type': 'application/json',
        ...options.headers
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    console.log(`🌐 API Request: ${options.method || 'GET'} ${endpoint}`, { hasToken: !!token });

    try {
        const response = await fetch(`${API_URL}${endpoint}`, {
            ...options,
            headers,
            credentials: 'include'
        });

        console.log(`📡 API Response: ${response.status} ${response.statusText}`, endpoint);

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'API request failed' }));
            console.error(`❌ API Error: ${response.status}`, error);

            // Handle authentication errors - redirect to login
            // Only clear and redirect if we're not on a public route
            if (response.status === 401) {
                const publicRoutes = ['/login', '/register', '/', '/about', '/gallery', '/privacy', '/terms'];
                const isPublicRoute = publicRoutes.includes(window.location.pathname);

                console.log('🔒 API: 401 Unauthorized', { isPublicRoute, currentPath: window.location.pathname });

                if (!isPublicRoute) {
                    // Clear invalid token
                    console.log('🔒 API: Clearing invalid session');
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    toast.error('Session expired. Please login again.');
                    // Redirect to login if not already there
                    if (window.location.pathname !== '/login') {
                        setTimeout(() => {
                            window.location.href = '/login';
                        }, 1500);
                    }
                }
            } else if (response.status >= 500) {
                toast.error('Server error. Please try again later.');
            } else if (response.status >= 400) {
                // Don't show toast for 401 (already handled) or if error message will be shown by caller
                if (response.status !== 401 && !options.silent) {
                    toast.error(error.error || error.message || 'Request failed');
                }
            }

            throw new Error(error.error || error.message || 'API request failed');
        }

        const data = await response.json();
        console.log(`✅ API Success:`, endpoint, data);
        return data;
    } catch (err) {
        console.error(`❌ API Request Failed:`, endpoint, err);
        // Only show toast for network errors or if not silent
        if (err.message === 'Failed to fetch' && !options.silent) {
            toast.error('Network error. Please check your connection.');
        }
        throw err;
    }
};

// Auth API
export const authAPI = {
    register: (userData) => apiRequest('/auth/register', {
        method: 'POST',
        body: JSON.stringify(userData)
    }),

    login: (credentials, options = {}) => apiRequest('/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
        ...options
    }),

    getCurrentUser: () => apiRequest('/auth/me'),

    sendOTP: (data) => apiRequest('/auth/send-otp', {
        method: 'POST',
        body: JSON.stringify(data)
    }),

    verifyOTP: (data) => apiRequest('/auth/verify-otp', {
        method: 'POST',
        body: JSON.stringify(data)
    })
};

// Profile API
export const profileAPI = {
    getProfile: () => apiRequest('/profile'),

    updateProfile: (profileData) => apiRequest('/profile', {
        method: 'PUT',
        body: JSON.stringify(profileData)
    }),

    getUser: (userId) => apiRequest(`/profile/${userId}`)
};

// ============ COMMUNITY API ============ //

export const communityAPI = {
    // POSTS
    getPosts: (filters = {}) => {
        const params = new URLSearchParams(filters).toString();
        return apiRequest(`/community/posts?${params}`);
    },

    createPost: (postData) => apiRequest('/community/posts', {
        method: 'POST',
        body: JSON.stringify(postData)
    }),

    getPost: (postId) => apiRequest(`/community/posts/${postId}`),

    likePost: (postId) => apiRequest(`/community/posts/${postId}/like`, {
        method: 'POST'
    }),

    // COMMENTS
    getComments: (postId) => apiRequest(`/community/posts/${postId}/comments`),

    addComment: (postId, content) => apiRequest(`/community/posts/${postId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ content })
    })
};


// ============ Q&A API ============ //
export const qandaAPI = {
    // QUESTIONS
    getQuestions: (filters = {}) => {
        const params = new URLSearchParams(filters).toString();
        return apiRequest(`/qanda/questions?${params}`);
    },

    createQuestion: (questionData) => apiRequest('/qanda/questions', {
        method: 'POST',
        body: JSON.stringify(questionData)
    }),

    getQuestion: (questionId) => apiRequest(`/qanda/questions/${questionId}`),

    markQuestionResolved: (questionId) => apiRequest(`/qanda/questions/${questionId}/resolve`, {
        method: 'POST'
    }),

    // ANSWERS
    getAnswers: (questionId) => apiRequest(`/qanda/questions/${questionId}/answers`),

    addAnswer: (questionId, content) => apiRequest(`/qanda/questions/${questionId}/answers`, {
        method: 'POST',
        body: JSON.stringify({ content })
    }),

    markAnswerHelpful: (answerId) => apiRequest(`/qanda/answers/${answerId}/helpful`, {
        method: 'POST'
    })
};


// ============ SESSION API ============ //
export const sessionAPI = {
    getActive: () => apiRequest('/sessions/active'),
    getAll: (filters = {}) => {
        const params = new URLSearchParams(filters).toString();
        return apiRequest(`/sessions?${params}`);
    },
    getById: (id) => apiRequest(`/sessions/${id}`),
    create: (data) => apiRequest('/sessions', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    update: (id, data) => apiRequest(`/sessions/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    delete: (id) => apiRequest(`/sessions/${id}`, {
        method: 'DELETE'
    })
};

// ============ APPLICATION API ============ //
export const applicationAPI = {
    createOrUpdate: (data) => apiRequest('/applications', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    getMyApplications: () => apiRequest('/applications/my-applications'),
    getById: (id) => apiRequest(`/applications/${id}`),
    downloadPDF: (id) => {
        const token = localStorage.getItem('token');
        return fetch(`${API_URL}/applications/${id}/download`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        }).then(async (response) => {
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to download PDF');
            }
            return response.blob();
        });
    },
    submit: (id) => apiRequest(`/applications/${id}/submit`, {
        method: 'POST'
    }),
    getBySession: (sessionId, filters = {}) => {
        const params = new URLSearchParams(filters).toString();
        return apiRequest(`/applications/session/${sessionId}?${params}`);
    },
    updateStatus: (id, status, notes) => apiRequest(`/applications/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status, internal_notes: notes })
    }),
    // Public application endpoints (no auth required)
    createOrUpdatePublic: (data) => apiRequest('/applications/public', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    submitPublic: (email, applicationId, password) => apiRequest('/applications/public/submit', {
        method: 'POST',
        body: JSON.stringify({ email, applicationId, password })
    })
};

// ============ EMPLOYER API ============ //
export const employerAPI = {
    register: (data) => apiRequest('/employers/register', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    uploadCompliance: (data) => apiRequest('/employers/compliance', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    getProfile: () => apiRequest('/employers/profile'),
    updateProfile: (data) => apiRequest('/employers/profile', {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    getAll: (filters = {}) => {
        // Convert boolean values to strings for URL params
        const queryParams = {};
        Object.keys(filters).forEach(key => {
            if (typeof filters[key] === 'boolean') {
                queryParams[key] = filters[key] ? '1' : '0';
            } else {
                queryParams[key] = filters[key];
            }
        });
        const params = new URLSearchParams(queryParams).toString();
        return apiRequest(`/employers?${params}`);
    },
    approve: (id) => apiRequest(`/employers/${id}/approve`, {
        method: 'POST'
    }),
    submitKYC: (data) => apiRequest('/employers/kyc/submit', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    uploadKYCDocuments: (data) => apiRequest('/employers/kyc/documents', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    reviewKYC: (id, data) => apiRequest(`/employers/kyc/${id}/review`, {
        method: 'POST',
        body: JSON.stringify(data)
    })
};

// ============ JOB API ============ //
export const jobAPI = {
    create: (data) => apiRequest('/jobs', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    getAll: (filters = {}) => {
        const params = new URLSearchParams(filters).toString();
        return apiRequest(`/jobs?${params}`);
    },
    getById: (id) => apiRequest(`/jobs/${id}`),
    getMyJobs: () => apiRequest('/jobs/employer/my-jobs'),
    apply: (id, data) => apiRequest(`/jobs/${id}/apply`, {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    getApplications: (id) => apiRequest(`/jobs/${id}/applications`),
    updateApplicationStatus: (id, status) => apiRequest(`/jobs/applications/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status })
    })
};

// ============ ANNOUNCEMENT API ============ //
export const announcementAPI = {
    create: (data) => apiRequest('/announcements', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    getAll: (filters = {}) => {
        const params = new URLSearchParams(filters).toString();
        return apiRequest(`/announcements?${params}`);
    },
    getMyFeed: () => apiRequest('/announcements/my/feed'),
    getById: (id) => apiRequest(`/announcements/${id}`),
    update: (id, data) => apiRequest(`/announcements/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    delete: (id) => apiRequest(`/announcements/${id}`, {
        method: 'DELETE'
    })
};

// ============ EVENTS API ============ //
export const eventAPI = {
    getPublic: (limit = 6, options = {}) => {
        const params = new URLSearchParams({ limit: String(limit) });
        if (options.upcomingOnly) {
            params.set('upcoming', '1');
        }
        return apiRequest(`/events?${params.toString()}`, { silent: true });
    },
    getById: (id) => apiRequest(`/events/${id}`, { silent: true }),
    getAllAdmin: () => apiRequest('/admin/events'),
    create: (data) => apiRequest('/admin/events', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    update: (id, data) => apiRequest(`/admin/events/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
    }),
    remove: (id) => apiRequest(`/admin/events/${id}`, {
        method: 'DELETE'
    })
};

// ============ ALUMNI API ============ //
export const alumniAPI = {
    apply: (data) => apiRequest('/alumni/apply', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    getMyApplication: () => apiRequest('/alumni/my-application'),
    getAll: (filters = {}) => {
        const params = new URLSearchParams(filters).toString();
        return apiRequest(`/alumni?${params}`);
    },
    approve: (id, notes) => apiRequest(`/alumni/${id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ notes })
    }),
    reject: (id, notes) => apiRequest(`/alumni/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ notes })
    })
};

// ============ INTERVIEW API ============ //
export const interviewAPI = {
    schedule: (data) => apiRequest('/interviews', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    getByApplication: (applicationId) => apiRequest(`/interviews/application/${applicationId}`),
    getMyInterviews: () => apiRequest('/interviews/my-interviews')
};

// ============ NOTIFICATION API ============ //
export const notificationAPI = {
    getAll: (unreadOnly = false) => apiRequest(`/notifications?unread_only=${unreadOnly}`),
    getUnreadCount: () => apiRequest('/notifications/unread-count'),
    markAsRead: (id) => apiRequest(`/notifications/${id}/read`, {
        method: 'PATCH'
    }),
    markAllAsRead: () => apiRequest('/notifications/read-all', {
        method: 'PATCH'
    })
};

// ============ MESSAGE API ============ //
export const messageAPI = {
    send: (data) => apiRequest('/messages', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    getInbox: () => apiRequest('/messages/inbox'),
    getSent: () => apiRequest('/messages/sent'),
    markAsRead: (id) => apiRequest(`/messages/${id}/read`, {
        method: 'PATCH'
    })
};

// ============ ADMIN API ============ //
export const adminAPI = {
    getDashboardStats: () => apiRequest('/admin/dashboard/stats'),
    getAllUsers: (filters = {}) => {
        const params = new URLSearchParams(filters).toString();
        return apiRequest(`/admin/users?${params}`);
    },
    getById: (id) => apiRequest(`/admin/users/${id}`),
    updateUserRole: (id, role) => apiRequest(`/admin/users/${id}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role })
    }),
    toggleUserStatus: (id, isActive) => apiRequest(`/admin/users/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: isActive })
    }),
    createUser: (data) => apiRequest('/admin/users', {
        method: 'POST',
        body: JSON.stringify(data)
    }),
    createStudentManager: (data) => apiRequest('/admin/student-managers', {
        method: 'POST',
        body: JSON.stringify(data)
    })
};

// ============ OTP API ============ //
export const otpAPI = {
    send: (email) => apiRequest('/auth/send-otp', {
        method: 'POST',
        body: JSON.stringify({ email })
    }),
    verify: (email, otp) => apiRequest('/auth/verify-otp', {
        method: 'POST',
        body: JSON.stringify({ email, otp })
    })
};

// ============ UPLOAD API ============ //
export const donorRequestAPI = {
    create: async (data) => {
        return apiRequest('/donor-requests', {
            method: 'POST',
            body: JSON.stringify(data)
        });
    },
    getAll: async (filters = {}) => {
        const queryParams = new URLSearchParams(filters).toString();
        return apiRequest(`/donor-requests${queryParams ? `?${queryParams}` : ''}`);
    },
    getById: async (id) => {
        return apiRequest(`/donor-requests/${id}`);
    },
    updateStatus: async (id, status, notes) => {
        return apiRequest(`/donor-requests/${id}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status, notes })
        });
    }
};

export const uploadAPI = {
    uploadFile: async (file, isPublic = false) => {
        const formData = new FormData();
        formData.append('file', file);

        const token = localStorage.getItem('token');
        const headers = {};
        if (token && !isPublic) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        // Use public endpoint if no token or explicitly public
        const endpoint = (isPublic || !token) ? '/upload/public/single' : '/upload/single';

        const response = await fetch(`${API_URL}${endpoint}`, {
            method: 'POST',
            headers,
            body: formData
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Upload failed' }));
            throw new Error(error.error || 'Upload failed');
        }

        return response.json();
    },
    uploadFiles: async (files) => {
        const formData = new FormData();
        files.forEach(file => {
            formData.append('files', file);
        });

        const token = localStorage.getItem('token');
        const headers = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_URL}/upload/multiple`, {
            method: 'POST',
            headers,
            body: formData
        });

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Upload failed' }));
            throw new Error(error.error || 'Upload failed');
        }

        return response.json();
    }
};

export default {
    auth: authAPI,
    profile: profileAPI,
    community: communityAPI,
    qanda: qandaAPI,
    session: sessionAPI,
    application: applicationAPI,
    employer: employerAPI,
    job: jobAPI,
    announcement: announcementAPI,
    alumni: alumniAPI,
    interview: interviewAPI,
    notification: notificationAPI,
    message: messageAPI,
    admin: adminAPI,
    event: eventAPI,
    otp: otpAPI,
    upload: uploadAPI,
    donorRequest: donorRequestAPI
};